import React, { useEffect, useState, useRef } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Grid,
    Box,
    styled,
    Skeleton,
} from "@mui/material";
import {
    Menu as MenuIcon,
    Search,
    ArrowBackIos,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonIcon from '@mui/icons-material/Person';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import pravachanIcon from '../../../src/svg/pravachanHome.svg';
import audioIcon from '../../../src/svg/audioHome.svg';
import ArticleIcon from '@mui/icons-material/Article';
import VideocamIcon from '@mui/icons-material/Videocam';
import MuiAccordionSummary, {
    AccordionSummaryProps,
    accordionSummaryClasses,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { useNavigate } from "react-router-dom";
import ArticlesService from "../../Services/Articles";
import VideoService from "../../Services/VideoPlayer";
import { toSeoSlug } from "../../utils/seoSlug";
// import MobileHome from "./MobileHome";

// --- Styled Accordion Components ---

const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&::before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
        expandIcon={<KeyboardArrowDownIcon sx={{ fontSize: '0.9rem' }} />} // Use the down arrow icon
        {...props}
    />
))(({ theme }) => ({
    backgroundColor: '#FFEBD5',
    flexDirection: 'row', // Content then icon
    [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
        transform: 'rotate(0deg)', // Down arrow points down by default
    },
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
        transform: 'rotate(180deg)', // Rotate 180 degrees to point up when expanded
    },
    [`& .${accordionSummaryClasses.content}`]: {
        marginLeft: 0,
        flexGrow: 1,
        justifyContent: 'flex-start',
        paddingRight: theme.spacing(1), // Space between text and arrow
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none'
}));

// --- Authors Accordion Component ---

export interface CategoryItem {
    id: number;
    name: string;
}

export interface KalyansItem {
    id: number;
    name: string;
}
export interface KalpataruItem {
    id: number;
    name: string;
}
export interface VivekVaniItem {
    id: number;
    name: string
}

export interface AudioItem {
    id: number;
    name: string;
}
export interface PravachanItem {
    id: number | string;
    name: string;
}
export interface VideoItem {
    id: number;
    name: string;
    slug?: string;
}
export interface ArticleItem {
    id: number;
    name: string;
    slug?: string;
}
export interface SubjectItem {
    id: number;
    name: string;
}
export interface audiosItem {
    id: number;
    name: string;
}
export interface videosItem {
    id: number;
    name: string;
    slug?: string;
}
export interface articlesItem {
    id: number;
    name: string;
    slug?: string;
}
export interface AuthorsAccordionProps {
    Authors: CategoryItem[];
    kalyans: KalyansItem[];
    loadingAuthors: boolean; // Prop to indicate loading state
    kalpataru: KalpataruItem[];
    vivekVani: VivekVaniItem[];
    audios: audiosItem[];
    pravachans: PravachanItem[];
    videos: videosItem[];
    articles: articlesItem[];
    audioAuthors: audiosItem[]; // New prop for audio authors
    articlesAuthors: articlesItem[]; // New prop for articles authors
    videoAuthors: videosItem[]; // New prop for video authors
    setProductFor: (value: string) => void; // Prop to set productFor
}

const AuthorsAccordion: React.FC<AuthorsAccordionProps> = ({ Authors, kalyans, loadingAuthors, kalpataru, vivekVani, audios, pravachans, videos, articles, setProductFor, audioAuthors, articlesAuthors, videoAuthors }) => {
    const [expanded, setExpanded] = useState<string | false>(false);

    const { t } = useTranslation();

    // Helper to set both expanded and productFor
    const handleChange = (panelId: string, productForValue: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
        // debugger;
        setExpanded(newExpanded ? panelId : false);
        if (newExpanded) setProductFor(productForValue);
    };
    const navigate = useNavigate();

    const resolveAuthorSlug = async (
        item: { id?: string | number; slug?: string },
        fetchSlug: (id: string) => Promise<any>,
        logPrefix: string
    ): Promise<string> => {
        const logdetails: string[] = [logPrefix, `id=${item.id || "none"}`, `slug=${item.slug || "none"}`];
        let slug = toSeoSlug(item);
        if (!slug && item.id) {
            try {
                const res = await fetchSlug(String(item.id));
                slug = toSeoSlug({ slug: res?.result?.slug, id: res?.result?.id });
                logdetails.push(`resolved_slug=${slug || "none"}`);
            } catch (error: any) {
                logdetails.push(`resolve_error=${error?.message || "unknown"}`);
            }
        }
        if (!slug) {
            logdetails.push("abort=missing_slug");
            console.log("[AuthorsAccordion]", logdetails.join(" | "));
            return "";
        }
        logdetails.push(`seo_slug=${slug}`);
        console.log("[AuthorsAccordion]", logdetails.join(" | "));
        return slug;
    };

    return (
        <>
            <Box sx={{ p: 2 }} >
                {loadingAuthors ? (
                    <Box sx={{ p: 2 }}>
                        {[
                            { width: '60%' },
                            { width: '50%' },
                            { width: '55%' },
                            { width: '45%' },
                            { width: '40%' },
                        ].map((header, index) => (
                            <Accordion
                                key={index}
                                expanded={false}
                                sx={{
                                    backgroundColor: "#FFEBD5",
                                    margin: index === 0 ? -14 : "-14px",
                                    marginTop: index === 0 ? -35 : "10px",
                                    border: "2px solid white",
                                    '&.Mui-expanded': {
                                        margin: "-14px",
                                        marginTop: index === 0 ? -35 : "10px",
                                        minHeight: 'auto',
                                    },
                                    '& .MuiAccordionSummary-root': {
                                        minHeight: '48px',
                                    }
                                }}
                            >
                                <AccordionSummary expandIcon={null} sx={{ cursor: 'default' }}>
                                    <Skeleton variant="text" width={header.width} height={28} />
                                </AccordionSummary>
                                <AccordionDetails style={{ backgroundColor: "white" }}>
                                    {Array.from(new Array(3)).map((_, i) => (
                                        <Box key={i} sx={{ borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
                                            <Skeleton variant="text" width={`${80 - (i % 3) * 10}%`} height={20} />
                                        </Box>
                                    ))}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                ) : (
                    <>
                        {Authors.length === 0 && kalyans.length === 0 ? (
                            <Typography sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
                                No Authors or Kalyans found.
                            </Typography>
                        ) : (
                            <>
                                {/* E_books_tr Accordion */}
                                <Accordion
                                    style={{ backgroundColor: "#FFEBD5", margin: -14, marginTop: -35, border: "2px solid white" }}
                                    expanded={expanded === 'eBooksPanel'}
                                    // onClick={() => setProductFor('book')}
                                    onChange={handleChange('eBooksPanel', 'book')}
                                >
                                    <AccordionSummary>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <MenuBookIcon sx={{ fontSize: 18, color: "#f97316" }} />
                                            <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("E_books_tr")}</Typography>
                                        </Box>
                                    </AccordionSummary>

                                    <AccordionDetails style={{ backgroundColor: "white" }}>
                                        {Authors.length > 0 ? (
                                            Authors.map((category: CategoryItem) => (
                                                <Box key={category.id} onClick={() => navigate("/books", { state: { selectedAuthorId: category.id, authorName: category.name } })} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2, cursor: 'pointer' }}>
                                                    <PersonIcon sx={{ fontSize: 14, color: '#f97316' }} />
                                                    <Typography component="span">{category.name}</Typography>
                                                </Box>
                                            ))
                                        ) : (
                                            <Typography sx={{ color: 'text.secondary', fontSize: 12, paddingLeft: 2 }}>
                                                No E-book Authors found.
                                            </Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion
                                    sx={{
                                        backgroundColor: "#FFEBD5",
                                        margin: "-14px",
                                        marginTop: "10px",
                                        border: "2px solid white",
                                        '&.Mui-expanded': {
                                            margin: "-14px",
                                            marginTop: "10px",
                                            minHeight: 'auto',
                                        },
                                        '& .MuiAccordionSummary-root': {
                                            minHeight: '48px',
                                        }
                                    }}
                                    expanded={expanded === 'audioPanel'}
                                    onChange={handleChange('audioPanel', 'audio')}
                                // onClick={() => setProductFor('audio')}
                                >
                                    <AccordionSummary>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <img src={audioIcon} alt="Audios" style={{ width: 18, height: 18 }} />
                                            <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("Audios_tr")}</Typography>
                                        </Box>
                                    </AccordionSummary>

                                    <AccordionDetails style={{ backgroundColor: "white" }}>
                                        {

                                            audioAuthors?.map((audioAuthor: audiosItem) => (
                                                <Box key={audioAuthor.id} onClick={() => navigate("/mobileAudioListPage", { state: { type: "audios", authorId: audioAuthor.id, authorName: audioAuthor.name } })} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2, cursor: 'pointer' }}>
                                                    <PersonIcon sx={{ fontSize: 14, color: '#f97316' }} />
                                                    <Typography component="span">{audioAuthor.name}</Typography>
                                                </Box>
                                            ))
                                        }
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion
                                    sx={{
                                        backgroundColor: "#FFEBD5",
                                        margin: "-14px",
                                        marginTop: "10px",
                                        border: "2px solid white",
                                        '&.Mui-expanded': {
                                            margin: "-14px",
                                            marginTop: "10px",
                                            minHeight: 'auto',
                                        },
                                        '& .MuiAccordionSummary-root': {
                                            minHeight: '48px',
                                        }
                                    }}
                                    expanded={expanded === 'articlePanel'}
                                    onChange={handleChange('articlePanel', 'article')}
                                // onClick={() => setProductFor('audio')}
                                >
                                    <AccordionSummary>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ArticleIcon sx={{ fontSize: 18, color: "#f97316" }} />
                                            <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("Article_tr")}</Typography>
                                        </Box>
                                    </AccordionSummary>

                                    <AccordionDetails style={{ backgroundColor: "white" }}>
                                        {

                                            articlesAuthors?.map((articleAuthor: articlesItem) => (
                                                <Box onClick={() => {
                                                    void (async () => {
                                                        const slug = await resolveAuthorSlug(articleAuthor, (id) => ArticlesService.GetAuthorDataById(id), "article_author_click");
                                                        if (!slug) return;
                                                        navigate(`/articles/author/${slug}`, {
                                                            state: {
                                                                selectedAuthorId: articleAuthor.id,
                                                                authorId: articleAuthor.id,
                                                                authorName: articleAuthor.name,
                                                                authorSlug: slug,
                                                                productType: "article",
                                                            },
                                                        });
                                                    })();
                                                }} key={articleAuthor.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2, cursor: 'pointer' }}>
                                                    <PersonIcon sx={{ fontSize: 14, color: '#f97316' }} />
                                                    <Typography component="span">{articleAuthor.name}</Typography>
                                                </Box>
                                            ))
                                        }
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion
                                    sx={{
                                        backgroundColor: "#FFEBD5",
                                        margin: "-14px",
                                        marginTop: "10px",
                                        border: "2px solid white",
                                        '&.Mui-expanded': {
                                            margin: "-14px",
                                            marginTop: "10px",
                                            minHeight: 'auto',
                                        },
                                        '& .MuiAccordionSummary-root': {
                                            minHeight: '48px',
                                        }
                                    }}
                                    expanded={expanded === 'pravachanPanel'}
                                    onChange={handleChange('pravachanPanel', 'pravachan')}
                                >
                                    <AccordionSummary>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <img src={pravachanIcon} alt="Pravachan" style={{ width: 18, height: 18 }} />
                                            <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("Pravachan_tr")}</Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails style={{ backgroundColor: "white" }}>
                                        <Box onClick={() => navigate("/mobileAudioListPage", { state: { type: "pravachans", authorName: t("Honorable_Shri_Jayadayal_Ji") } })} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2, cursor: 'pointer' }}>
                                            <PersonIcon sx={{ fontSize: 14, color: '#f97316' }} />
                                            <Typography component="span">{t("Honorable_Shri_Jayadayal_Ji")}</Typography>
                                        </Box>
                                        <Box onClick={() => navigate("/mobileAudioListPage", { state: { type: "pravachans", authorName: t("Honorable_Shri_Hanumanprasad_ji_poddar") } })} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2, cursor: 'pointer' }}>
                                            <PersonIcon sx={{ fontSize: 14, color: '#f97316' }} />
                                            <Typography component="span">{t("Honorable_Shri_Hanumanprasad_ji_poddar")}</Typography>
                                        </Box>
                                        <Box onClick={() => navigate("/mobileAudioListPage", { state: { type: "pravachans", isSpecial: true, authorName: t("Honorable_Swami_Shri_Ramsukhdas_Ji"), sortOption: "4" } })} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2, cursor: 'pointer' }}>
                                            <PersonIcon sx={{ fontSize: 14, color: '#f97316' }} />
                                            <Typography component="span">{t("Honorable_Swami_Shri_Ramsukhdas_Ji")}</Typography>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion
                                    sx={{
                                        backgroundColor: "#FFEBD5",
                                        margin: "-14px",
                                        marginTop: "10px",
                                        border: "2px solid white",
                                        '&.Mui-expanded': {
                                            margin: "-14px",
                                            marginTop: "10px",
                                            minHeight: 'auto',
                                        },
                                        '& .MuiAccordionSummary-root': {
                                            minHeight: '48px',
                                        }
                                    }}
                                    expanded={expanded === 'videoPanel'}
                                    onChange={handleChange('videoPanel', 'video')}
                                >
                                    <AccordionSummary>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <VideocamIcon sx={{ fontSize: 18, color: "#f97316" }} />
                                            <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("Video_tr")}</Typography>
                                        </Box>
                                    </AccordionSummary>

                                    <AccordionDetails style={{ backgroundColor: "white" }}>
                                        {
                                            videoAuthors.map((video: VideoItem) => (
                                                <Box key={video.id} onClick={() => navigate("/videos", {
                                                    state: {
                                                        authorId: video.id,
                                                        authorName: video.name,
                                                    },
                                                })} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2, cursor: 'pointer' }}>
                                                    <PersonIcon sx={{ fontSize: 14, color: '#f97316' }} />
                                                    <Typography component="span">{video.name}</Typography>
                                                </Box>
                                            ))

                                        }
                                    </AccordionDetails>
                                </Accordion>
                            </>
                        )}
                    </>
                )}
            </Box>

        </>
    );

};
export default AuthorsAccordion;
// --- MobileHomePage Component ---

// --- Export AuthorsAccordion Component ---



